import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { closeAccount, deleteAccount } from 'api/account';
import { fetchTransactions } from 'api/transactions';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface CloseAccountFormValues {
    reason: string;
    feedback: string;
}

const DeleteOrCloseAccountSettings = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();

    const { data: transactions } = useSuspenseQuery({
        queryKey: transactionQueryKeys.transactions(accountId),
        queryFn: () => fetchTransactions(accountId),
        refetchOnWindowFocus: false,
    });
    const canDelete = transactions?.data.length === 0;

    const methods = Form.useGivebutterForm({
        defaultValues: {
            reason: '',
            feedback: '',
        },
    });

    const {
        control,
        formState: { isValid },
        handleSubmit,
        path,
        reset,
    } = methods;

    const { mutate: submitClose, isPending: isClosingAccount } = useMutation<
        unknown,
        GivebutterAxiosError,
        CloseAccountFormValues
    >({
        mutationFn: values => closeAccount(accountId, values),
        onSuccess: () => {
            success('Account successfully closed.');
            window.location.assign(window.location.origin);
        },
        onError: e => {
            error(e.response?.data.message || 'Could not close the account. Please contact us.');
        },
    });

    const { mutate: submitDelete, isPending: isDeletingAccount } = useMutation<
        unknown,
        GivebutterAxiosError,
        number
    >({
        mutationFn: deleteAccount,
        onSuccess: () => {
            success('Account successfully deleted.');
            window.location.assign(window.location.origin);
        },
        onError: e => {
            error(e.response?.data.message || "Couldn't delete account. Please contact us.");
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>
                    {canDelete ? 'Delete' : 'Close'} Account
                </SettingsCard.Heading>
                <p className="text-sm text-gray-700">
                    Once you {canDelete ? 'delete' : 'close'} your account, you will no longer be
                    able to access it and all data associated with your account will be deleted.
                </p>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                {canDelete ? (
                    <AlertDialog.Root type="error">
                        <AlertDialog.Trigger asChild={true}>
                            <Button variant="solid" color="error" disabled={isDeletingAccount}>
                                {isDeletingAccount && (
                                    <FontAwesomeIcon
                                        icon={faSpinnerThird}
                                        className="animate-spin"
                                    />
                                )}
                                Delete Account
                            </Button>
                        </AlertDialog.Trigger>
                        <AlertDialog.Portal>
                            <AlertDialog.Overlay />
                            <AlertDialog.Content>
                                <AlertDialog.Header>
                                    <AlertDialog.Icon />
                                    <AlertDialog.Close />
                                </AlertDialog.Header>
                                <AlertDialog.Body>
                                    <AlertDialog.Title>Delete Account?</AlertDialog.Title>
                                    <AlertDialog.Description>
                                        This will also delete your campaigns. You will not be able
                                        to undo this action. Are you sure?
                                    </AlertDialog.Description>
                                </AlertDialog.Body>
                                <AlertDialog.Footer>
                                    <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                    <AlertDialog.Action
                                        onClick={() => submitDelete(accountId)}
                                        disabled={isDeletingAccount}
                                    >
                                        Delete account
                                    </AlertDialog.Action>
                                </AlertDialog.Footer>
                            </AlertDialog.Content>
                        </AlertDialog.Portal>
                    </AlertDialog.Root>
                ) : (
                    <AlertDialog.Root type="error">
                        <AlertDialog.Trigger asChild={true}>
                            <Button variant="solid" color="error" disabled={isClosingAccount}>
                                {isClosingAccount && (
                                    <FontAwesomeIcon
                                        icon={faSpinnerThird}
                                        className="animate-spin"
                                    />
                                )}
                                Close Account
                            </Button>
                        </AlertDialog.Trigger>
                        <AlertDialog.Portal>
                            <AlertDialog.Overlay />
                            <AlertDialog.Content>
                                <Form.Provider {...methods}>
                                    <form
                                        onSubmit={handleSubmit(values => submitClose(values))}
                                        className="flex flex-col gap-4"
                                    >
                                        <AlertDialog.Header>
                                            <AlertDialog.Icon />
                                            <AlertDialog.Close />
                                        </AlertDialog.Header>
                                        <AlertDialog.Body>
                                            <AlertDialog.Title>Close Account?</AlertDialog.Title>
                                            <AlertDialog.Description>
                                                This will also delete your campaigns. You will not
                                                be able to undo this action. Are you sure?
                                            </AlertDialog.Description>
                                            <div className="flex flex-col gap-4">
                                                <Form.Container>
                                                    <Form.Label name={path('reason')}>
                                                        Reason
                                                    </Form.Label>
                                                    <Form.SingleSelect
                                                        control={control}
                                                        name="reason"
                                                        rules={{
                                                            required: 'Please select a reason',
                                                        }}
                                                    >
                                                        <SingleSelect.Trigger aria-label="Reason">
                                                            <SingleSelect.Value placeholder="Select a reason" />
                                                        </SingleSelect.Trigger>
                                                        <SingleSelect.Content>
                                                            {[
                                                                'Missing Features',
                                                                'No longer have a need for Givebutter',
                                                                'Unsatisfied with Givebutter',
                                                                'Other',
                                                            ].map(value => (
                                                                <SingleSelect.Item
                                                                    value={value}
                                                                    key={value}
                                                                >
                                                                    <SingleSelect.ItemText>
                                                                        {value}
                                                                    </SingleSelect.ItemText>
                                                                </SingleSelect.Item>
                                                            ))}
                                                        </SingleSelect.Content>
                                                    </Form.SingleSelect>
                                                    <Form.Error name={path('reason')} />
                                                </Form.Container>
                                                <Form.Container>
                                                    <Form.Label name={path('feedback')}>
                                                        Feedback
                                                    </Form.Label>
                                                    <Form.UseFormTextArea
                                                        control={control}
                                                        name="feedback"
                                                        placeholder="Please provide any feedback you have about Givebutter."
                                                        rows={5}
                                                    />
                                                    <Form.Error name={path('feedback')} />
                                                </Form.Container>
                                            </div>
                                        </AlertDialog.Body>
                                        <AlertDialog.Footer>
                                            <AlertDialog.Cancel onClick={() => reset()}>
                                                Cancel
                                            </AlertDialog.Cancel>
                                            <Form.SubmitButton
                                                className="w-1/2"
                                                isSubmitting={isClosingAccount}
                                                disabled={!isValid}
                                                color="error"
                                            >
                                                Close account
                                            </Form.SubmitButton>
                                        </AlertDialog.Footer>
                                    </form>
                                </Form.Provider>
                            </AlertDialog.Content>
                        </AlertDialog.Portal>
                    </AlertDialog.Root>
                )}
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { DeleteOrCloseAccountSettings };
